import { useState, useEffect, useCallback, useMemo } from "react";
import {
  X,
  CalendarCheck,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  Coins,
  ArrowRight,
  RefreshCw,
  Edit3,
  ShieldAlert,
  Building2,
  TrendingUp,
  Receipt,
  CreditCard,
  Banknote,
  RotateCcw,
  ArrowDownRight,
  Layers,
} from "lucide-react";
import { TransactionHistoryService } from "../../services/api/transactionHistory.service";
import {
  MonthEndLogItem,
  PaymentSummaryData,
} from "../../types/transactionHistory.types";
import toast from "react-hot-toast";

interface MonthEndLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  clinicId?: string;
  clinicName?: string;
  isAdmin?: boolean;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatCurrency(amount: number | undefined | null): string {
  if (amount === undefined || amount === null || isNaN(amount)) return "₹0";
  return `₹${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function MonthEndLogModal({
  isOpen,
  onClose,
  clinicId,
  clinicName,
  isAdmin = false,
}: MonthEndLogModalProps) {
  const currentDate = useMemo(() => new Date(), []);
  const [selectedMonth, setSelectedMonth] = useState<string>(
    MONTH_NAMES[currentDate.getMonth()]
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    currentDate.getFullYear()
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [existingLog, setExistingLog] = useState<MonthEndLogItem | null>(null);

  // Creation State
  const [paymentSummary, setPaymentSummary] =
    useState<PaymentSummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [enteredCash, setEnteredCash] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Admin Edit State
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editFormData, setEditFormData] = useState<Partial<MonthEndLogItem>>({});

  // Year options (e.g. current year - 3 to current year + 1)
  const yearOptions = useMemo(() => {
    const cur = new Date().getFullYear();
    return [cur - 2, cur - 1, cur, cur + 1];
  }, []);

  // Compute start and end date for chosen month & year
  const monthDateRange = useMemo(() => {
    const monthIndex = MONTH_NAMES.indexOf(selectedMonth);
    const validMonthIndex = monthIndex >= 0 ? monthIndex : currentDate.getMonth();
    const startDate = `${selectedYear}-${String(validMonthIndex + 1).padStart(2, "0")}-01`;
    const lastDay = new Date(selectedYear, validMonthIndex + 1, 0).getDate();
    const endDate = `${selectedYear}-${String(validMonthIndex + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    return { startDate, endDate };
  }, [selectedMonth, selectedYear, currentDate]);

  // Fetch month end log or payment summary
  const checkMonthEndLog = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setIsEditing(false);
    setExistingLog(null);
    setPaymentSummary(null);
    setEnteredCash("");

    try {
      const res = await TransactionHistoryService.getMonthEndLog({
        clinicId,
        month: selectedMonth,
        year: selectedYear,
      });

      if (res.success && res.data && (res.data.id || (res.data as any)._id)) {
        setExistingLog(res.data);
      } else {
        // No log found, fetch payment summary to calculate required fields
        setExistingLog(null);
        fetchPaymentSummaryForMonth();
      }
    } catch (err) {
      console.error("Error checking month-end log:", err);
      setExistingLog(null);
      fetchPaymentSummaryForMonth();
    } finally {
      setLoading(false);
    }
  }, [isOpen, clinicId, selectedMonth, selectedYear, monthDateRange]);

  const fetchPaymentSummaryForMonth = async () => {
    setSummaryLoading(true);
    try {
      const summaryRes = await TransactionHistoryService.getPaymentSummary({
        startDate: monthDateRange.startDate,
        endDate: monthDateRange.endDate,
        clinicId,
      });

      if (summaryRes.success && summaryRes.data) {
        setPaymentSummary(summaryRes.data);
      }
    } catch (err) {
      console.error("Error fetching payment summary for month:", err);
      toast.error("Failed to fetch payment summary for this month");
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      checkMonthEndLog();
    }
  }, [isOpen, checkMonthEndLog]);

  // Calculated metrics from Payment Summary
  const calculatedMetrics = useMemo(() => {
    if (!paymentSummary) {
      return {
        grossRevenue: 0,
        collectedOnline: 0,
        collectedCash: 0,
        totalRevenue: 0,
        totalRefund: 0,
        totalExpense: 0,
        totalDeposit: 0,
        cashRefund: 0,
        cashExpense: 0,
        cashDeposit: 0,
        systemRemainingCash: 0,
      };
    }

    const {
      overviewCards,
      grossSummary,
      returnsSummary,
      netSummary,
      expensesSummary,
      bankDepositsSummary,
      returnsBanner,
    } = paymentSummary;

    const grossRevenue =
      overviewCards?.totalGrossRevenue?.amount ??
      grossSummary?.totalAmount ??
      0;
    const collectedOnline =
      overviewCards?.totalOnlineCollected?.amount ??
      grossSummary?.onlineAmount ??
      0;
    const collectedCash =
      overviewCards?.totalCashCollected?.amount ??
      grossSummary?.cashAmount ??
      0;
    const totalRevenue =
      overviewCards?.totalNetRevenue?.amount ??
      netSummary?.totalAmount ??
      0;
    const totalRefund =
      overviewCards?.totalReturns?.amount ??
      returnsSummary?.totalAmount ??
      returnsBanner?.totalReturnAmount ??
      0;
    const totalExpense =
      overviewCards?.totalOtherExpenses?.amount ??
      expensesSummary?.totalAmount ??
      0;
    const totalDeposit =
      overviewCards?.totalBankDeposits?.amount ??
      bankDepositsSummary?.totalAmount ??
      0;

    // Specific cash components for cash reconciliation:
    // Total cash collected - cash refunds - cash expenses - cash deposited to bank
    const cashRefund =
      returnsSummary?.cashReturnAmount ??
      returnsSummary?.cashAmount ??
      0;
    const cashExpense =
      expensesSummary?.cashAmount ??
      expensesSummary?.totalAmount ??
      0;
    const cashDeposit =
      bankDepositsSummary?.cashAmount ??
      bankDepositsSummary?.totalAmount ??
      0;

    const remainingCashRaw =
      collectedCash - cashRefund - cashExpense - cashDeposit;
    const systemRemainingCash =
      Math.round((remainingCashRaw + Number.EPSILON) * 100) / 100;

    return {
      grossRevenue,
      collectedOnline,
      collectedCash,
      totalRevenue,
      totalRefund,
      totalExpense,
      totalDeposit,
      cashRefund,
      cashExpense,
      cashDeposit,
      systemRemainingCash,
    };
  }, [paymentSummary]);

  // Cash Validation Logic
  const parsedEnteredCash = parseFloat(enteredCash);
  const isEnteredValidNumber =
    enteredCash.trim() !== "" && !isNaN(parsedEnteredCash);
  const cashDifference = isEnteredValidNumber
    ? Math.round((parsedEnteredCash - calculatedMetrics.systemRemainingCash) * 100) / 100
    : null;
  const isCashMatching =
    isEnteredValidNumber &&
    Math.abs(parsedEnteredCash - calculatedMetrics.systemRemainingCash) < 0.01;

  // Handle Log Creation
  const handleCreateMonthEndLog = async () => {
    if (!isCashMatching) {
      toast.error(
        "Entered cash amount must match system remaining cash exactly."
      );
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        clinicId,
        clinic_id: clinicId,
        month: selectedMonth,
        year: selectedYear,
        grossRevenue: calculatedMetrics.grossRevenue,
        collectedOnline: calculatedMetrics.collectedOnline,
        collectedCash: calculatedMetrics.collectedCash,
        totalRevenue: calculatedMetrics.totalRevenue,
        totalRefund: calculatedMetrics.totalRefund,
        totalExpense: calculatedMetrics.totalExpense,
        totalDeposit: calculatedMetrics.totalDeposit,
        closingBalance: parsedEnteredCash,
      };

      const res = await TransactionHistoryService.createMonthEndLog(payload);
      if (res.success) {
        toast.success(`Month-End Log for ${selectedMonth} ${selectedYear} created!`);
        await checkMonthEndLog();
      } else {
        toast.error(res.message || "Failed to create month-end log");
      }
    } catch (err) {
      toast.error("Failed to create month-end log");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Start Admin Editing
  const handleStartEdit = () => {
    if (!existingLog) return;
    setEditFormData({
      grossRevenue: existingLog.grossRevenue,
      collectedOnline: existingLog.collectedOnline,
      collectedCash: existingLog.collectedCash,
      totalRevenue: existingLog.totalRevenue,
      totalRefund: existingLog.totalRefund,
      totalExpense: existingLog.totalExpense,
      totalDeposit: existingLog.totalDeposit,
      closingBalance: existingLog.closingBalance,
    });
    setIsEditing(true);
  };

  // Save Admin Edit
  const handleSaveAdminEdit = async () => {
    if (!existingLog) return;
    setSubmitting(true);
    try {
      const res = await TransactionHistoryService.updateMonthEndLog(
        existingLog.id,
        editFormData
      );
      if (res.success) {
        toast.success("Month-End Log updated successfully");
        setIsEditing(false);
        await checkMonthEndLog();
      } else {
        toast.error(res.message || "Failed to update month-end log");
      }
    } catch (err) {
      toast.error("Failed to update month-end log");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col relative animate-slideUp overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center text-secondary border border-secondary/20">
              <CalendarCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-gray-900">
                  Month-End Closing Log
                </h3>
                {clinicName && (
                  <span className="text-xs px-2 py-0.5 font-medium bg-gray-100 text-gray-700 rounded-full">
                    {clinicName}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Monthly revenue, expense bifurcation & cash closing audit
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Month & Year Selection Bar */}
        <div className="px-5 py-3.5 bg-gray-50/80 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 shadow-sm">
              <span className="text-xs font-semibold text-gray-500">Month:</span>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="text-xs font-semibold text-gray-800 bg-transparent focus:outline-none cursor-pointer"
              >
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 shadow-sm">
              <span className="text-xs font-semibold text-gray-500">Year:</span>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="text-xs font-semibold text-gray-800 bg-transparent focus:outline-none cursor-pointer"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={checkMonthEndLog}
              title="Refresh log data"
              className="p-1.5 text-gray-500 hover:text-secondary hover:bg-secondary/10 rounded-lg transition-colors cursor-pointer"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Status Badge */}
          {!loading && (
            <div>
              {existingLog ? (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  Log Recorded
                </div>
              ) : (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                  Pending Log
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading ? (
            <div className="py-14 flex flex-col items-center justify-center text-center space-y-3">
              <div className="w-10 h-10 border-3 border-secondary/20 border-t-secondary rounded-full animate-spin" />
              <p className="text-xs font-medium text-gray-500">
                Checking month-end log records...
              </p>
            </div>
          ) : existingLog ? (
            /* ─────────────────────────────────────────────────────────────
               VIEW 1: EXISTING LOG DETAILS (WITH ADMIN EDIT CAPABILITY)
               ───────────────────────────────────────────────────────────── */
            <div className="space-y-5 animate-fadeIn">
              {/* Top Banner */}
              <div className="p-4 rounded-xl bg-emerald-50/70 border border-emerald-100 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700 mt-0.5">
                    <FileSpreadsheet className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-emerald-900">
                      Month-End Log is Finalized
                    </h4>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      Closing records for <b>{existingLog.month} {existingLog.year}</b> have been saved.
                    </p>
                    <div className="flex items-center gap-4 text-[11px] text-emerald-600 mt-2 font-medium">
                      <span>
                        Recorded: {new Date(existingLog.createdAt).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                {isAdmin && !isEditing && (
                  <button
                    onClick={handleStartEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-secondary bg-white border border-secondary/30 rounded-lg hover:bg-secondary/5 transition-all shadow-sm cursor-pointer"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    Edit Log
                  </button>
                )}
              </div>

              {/* View or Edit Details Grid */}
              {isEditing ? (
                /* Admin Edit Form */
                <div className="space-y-4 bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                      Admin Update Mode
                    </span>
                    <span className="text-[11px] text-amber-600 font-medium">
                      Modify fields with caution
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                        Gross Revenue
                      </label>
                      <input
                        type="number"
                        value={editFormData.grossRevenue ?? 0}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            grossRevenue: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                        Collected Online
                      </label>
                      <input
                        type="number"
                        value={editFormData.collectedOnline ?? 0}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            collectedOnline: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                        Collected Cash
                      </label>
                      <input
                        type="number"
                        value={editFormData.collectedCash ?? 0}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            collectedCash: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                        Total Net Revenue
                      </label>
                      <input
                        type="number"
                        value={editFormData.totalRevenue ?? 0}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            totalRevenue: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                        Total Refund
                      </label>
                      <input
                        type="number"
                        value={editFormData.totalRefund ?? 0}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            totalRefund: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                        Total Expense
                      </label>
                      <input
                        type="number"
                        value={editFormData.totalExpense ?? 0}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            totalExpense: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                        Total Deposit
                      </label>
                      <input
                        type="number"
                        value={editFormData.totalDeposit ?? 0}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            totalDeposit: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                        Closing Cash Balance
                      </label>
                      <input
                        type="number"
                        value={editFormData.closingBalance ?? 0}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            closingBalance: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white font-bold text-secondary"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
                    <button
                      onClick={() => setIsEditing(false)}
                      disabled={submitting}
                      className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveAdminEdit}
                      disabled={submitting}
                      className="px-4 py-1.5 text-xs font-semibold text-white bg-secondary rounded-lg hover:opacity-90 disabled:opacity-50 cursor-pointer"
                    >
                      {submitting ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              ) : (
                /* Read-only Display Cards */
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                      <TrendingUp className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-[11px] font-medium">Gross Revenue</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      {formatCurrency(existingLog.grossRevenue)}
                    </span>
                  </div>

                  <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                      <CreditCard className="w-3.5 h-3.5 text-sky-500" />
                      <span className="text-[11px] font-medium">Online Collected</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      {formatCurrency(existingLog.collectedOnline)}
                    </span>
                  </div>

                  <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                      <Banknote className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-[11px] font-medium">Cash Collected</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      {formatCurrency(existingLog.collectedCash)}
                    </span>
                  </div>

                  <div className="p-3.5 bg-emerald-50/50 rounded-xl border border-emerald-100">
                    <div className="flex items-center gap-1.5 text-emerald-700 mb-1">
                      <Receipt className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="text-[11px] font-semibold">Total Net Revenue</span>
                    </div>
                    <span className="text-sm font-bold text-emerald-700">
                      {formatCurrency(existingLog.totalRevenue)}
                    </span>
                  </div>

                  <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                      <RotateCcw className="w-3.5 h-3.5 text-rose-500" />
                      <span className="text-[11px] font-medium">Total Refund</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      {formatCurrency(existingLog.totalRefund)}
                    </span>
                  </div>

                  <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                      <ArrowDownRight className="w-3.5 h-3.5 text-orange-500" />
                      <span className="text-[11px] font-medium">Total Expenses</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      {formatCurrency(existingLog.totalExpense)}
                    </span>
                  </div>

                  <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                      <Building2 className="w-3.5 h-3.5 text-indigo-500" />
                      <span className="text-[11px] font-medium">Bank Deposits</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      {formatCurrency(existingLog.totalDeposit)}
                    </span>
                  </div>

                  <div className="p-3.5 bg-secondary/10 rounded-xl border border-secondary/30">
                    <div className="flex items-center gap-1.5 text-secondary mb-1">
                      <Coins className="w-3.5 h-3.5" />
                      <span className="text-[11px] font-bold">Closing Balance</span>
                    </div>
                    <span className="text-sm font-black text-secondary">
                      {formatCurrency(existingLog.closingBalance)}
                    </span>
                  </div>
                </div>
              )}

              {!isAdmin && (
                <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600">
                  <ShieldAlert className="w-4 h-4 text-gray-400 shrink-0" />
                  <span>
                    Clinics cannot edit month-end records after submission. Contact an administrator for modifications.
                  </span>
                </div>
              )}
            </div>
          ) : (
            /* ─────────────────────────────────────────────────────────────
               VIEW 2: ADD MONTH-END LOG (CALCULATE & RECONCILE CASH)
               ───────────────────────────────────────────────────────────── */
            <div className="space-y-5 animate-fadeIn">
              {/* Notice */}
              <div className="p-4 rounded-xl bg-amber-50/70 border border-amber-100 flex items-start gap-3">
                <div className="p-2 rounded-lg bg-amber-100 text-amber-700 mt-0.5">
                  <Coins className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-amber-900">
                    Month-End Closing Pending
                  </h4>
                  <p className="text-xs text-amber-800 mt-0.5">
                    Review the system-calculated financial summary for{" "}
                    <b>
                      {selectedMonth} {selectedYear}
                    </b>{" "}
                    and verify physical cash in hand to record closing.
                  </p>
                </div>
              </div>

              {summaryLoading ? (
                <div className="py-8 flex flex-col items-center justify-center text-center space-y-2">
                  <div className="w-7 h-7 border-2 border-secondary/20 border-t-secondary rounded-full animate-spin" />
                  <p className="text-xs text-gray-400">
                    Calculating payment summary for {selectedMonth} {selectedYear}...
                  </p>
                </div>
              ) : (
                <>
                  {/* Monthly Financial Breakdown */}
                  <div>
                    <h5 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-secondary" />
                      1. Monthly Summary Breakdown
                    </h5>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <span className="block text-[10px] font-medium text-gray-500 uppercase">
                          Gross Revenue
                        </span>
                        <span className="text-xs font-bold text-gray-800 mt-0.5 block">
                          {formatCurrency(calculatedMetrics.grossRevenue)}
                        </span>
                      </div>

                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <span className="block text-[10px] font-medium text-gray-500 uppercase">
                          Online Collected
                        </span>
                        <span className="text-xs font-bold text-sky-700 mt-0.5 block">
                          {formatCurrency(calculatedMetrics.collectedOnline)}
                        </span>
                      </div>

                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <span className="block text-[10px] font-medium text-gray-500 uppercase">
                          Cash Collected
                        </span>
                        <span className="text-xs font-bold text-amber-700 mt-0.5 block">
                          {formatCurrency(calculatedMetrics.collectedCash)}
                        </span>
                      </div>

                      <div className="p-3 bg-emerald-50/60 rounded-xl border border-emerald-100">
                        <span className="block text-[10px] font-semibold text-emerald-800 uppercase">
                          Net Revenue
                        </span>
                        <span className="text-xs font-bold text-emerald-700 mt-0.5 block">
                          {formatCurrency(calculatedMetrics.totalRevenue)}
                        </span>
                      </div>

                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <span className="block text-[10px] font-medium text-gray-500 uppercase">
                          Total Refunds
                        </span>
                        <span className="text-xs font-bold text-rose-600 mt-0.5 block">
                          {formatCurrency(calculatedMetrics.totalRefund)}
                        </span>
                      </div>

                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <span className="block text-[10px] font-medium text-gray-500 uppercase">
                          Total Expenses
                        </span>
                        <span className="text-xs font-bold text-orange-600 mt-0.5 block">
                          {formatCurrency(calculatedMetrics.totalExpense)}
                        </span>
                      </div>

                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <span className="block text-[10px] font-medium text-gray-500 uppercase">
                          Bank Deposits
                        </span>
                        <span className="text-xs font-bold text-indigo-600 mt-0.5 block">
                          {formatCurrency(calculatedMetrics.totalDeposit)}
                        </span>
                      </div>

                      <div className="p-3 bg-secondary/5 rounded-xl border border-secondary/20">
                        <span className="block text-[10px] font-semibold text-secondary uppercase">
                          Expected Cash
                        </span>
                        <span className="text-xs font-bold text-secondary mt-0.5 block">
                          {formatCurrency(calculatedMetrics.systemRemainingCash)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Cash Reconciliation Formula Box */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100/70 p-4 rounded-xl border border-gray-200/80 space-y-3">
                    <h5 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                      <Banknote className="w-3.5 h-3.5 text-emerald-600" />
                      2. System Cash Reconciliation
                    </h5>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                      <div className="p-2 bg-white rounded-lg border border-gray-200/70">
                        <span className="text-[11px] text-gray-500 block">Cash Collected (+)</span>
                        <span className="font-bold text-emerald-600">
                          {formatCurrency(calculatedMetrics.collectedCash)}
                        </span>
                      </div>
                      <div className="p-2 bg-white rounded-lg border border-gray-200/70">
                        <span className="text-[11px] text-gray-500 block">Cash Refunds (-)</span>
                        <span className="font-bold text-rose-600">
                          {formatCurrency(calculatedMetrics.cashRefund)}
                        </span>
                      </div>
                      <div className="p-2 bg-white rounded-lg border border-gray-200/70">
                        <span className="text-[11px] text-gray-500 block">Cash Expenses (-)</span>
                        <span className="font-bold text-orange-600">
                          {formatCurrency(calculatedMetrics.cashExpense)}
                        </span>
                      </div>
                      <div className="p-2 bg-white rounded-lg border border-gray-200/70">
                        <span className="text-[11px] text-gray-500 block">Bank Deposits (-)</span>
                        <span className="font-bold text-indigo-600">
                          {formatCurrency(calculatedMetrics.cashDeposit)}
                        </span>
                      </div>
                    </div>

                    <div className="p-3 bg-white rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-700">
                        System-Calculated Remaining Cash:
                      </span>
                      <span className="text-sm font-black text-secondary">
                        {formatCurrency(calculatedMetrics.systemRemainingCash)}
                      </span>
                    </div>
                  </div>

                  {/* Step 3: Physical Cash Entry and Matching */}
                  <div className="space-y-3 pt-1">
                    <h5 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                      <Coins className="w-3.5 h-3.5 text-secondary" />
                      3. Physical Cash Verification
                    </h5>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Enter Physical Cash Amount in Hand (₹){" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-sm">
                          ₹
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          placeholder={`Enter amount (e.g. ${calculatedMetrics.systemRemainingCash})`}
                          value={enteredCash}
                          onChange={(e) => setEnteredCash(e.target.value)}
                          className={`w-full pl-8 pr-4 py-2.5 text-sm font-semibold rounded-xl border focus:outline-none transition-all ${
                            !enteredCash.trim()
                              ? "border-gray-200 focus:border-secondary/50 focus:ring-2 focus:ring-secondary/10"
                              : isCashMatching
                              ? "border-emerald-500 bg-emerald-50/20 text-emerald-900 focus:ring-2 focus:ring-emerald-200"
                              : "border-rose-400 bg-rose-50/20 text-rose-900 focus:ring-2 focus:ring-rose-200"
                          }`}
                        />
                      </div>
                    </div>

                    {/* Feedback on matching */}
                    {enteredCash.trim() !== "" && (
                      <div className="animate-fadeIn">
                        {isCashMatching ? (
                          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-medium">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                            <span>
                              <b>Matches perfectly!</b> Physical cash in hand matches the system-calculated closing balance of <b>{formatCurrency(calculatedMetrics.systemRemainingCash)}</b>.
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-start gap-2.5 p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 font-medium">
                            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                            <div>
                              <span>
                                <b>Amount mismatch:</b> Entered amount (₹{parsedEnteredCash || 0}) does not match the system balance (₹{calculatedMetrics.systemRemainingCash}).
                              </span>
                              {cashDifference !== null && (
                                <span className="block mt-0.5 text-rose-700 font-bold">
                                  Difference: {cashDifference > 0 ? `+₹${cashDifference} (Surplus)` : `-₹${Math.abs(cashDifference)} (Shortage)`}
                                </span>
                              )}
                              <span className="block mt-1 text-[11px] text-rose-600">
                                Clinics cannot submit until the physical cash matches the calculated system balance. Please reconcile daily expenses or bank deposit entries if discrepancies exist.
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
          >
            Close
          </button>

          {!loading && !existingLog && (
            <button
              onClick={handleCreateMonthEndLog}
              disabled={!isCashMatching || submitting || summaryLoading}
              className={`flex items-center gap-2 px-5 py-2.5 text-xs font-bold text-white rounded-xl transition-all shadow-sm ${
                isCashMatching && !submitting && !summaryLoading
                  ? "bg-secondary hover:opacity-95 cursor-pointer shadow-secondary/20"
                  : "bg-gray-300 cursor-not-allowed opacity-70"
              }`}
            >
              {submitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Submitting Log...</span>
                </>
              ) : (
                <>
                  <span>Submit Month-End Log</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
